/**
 * EMAIL ADDRESS THUNKS — Async thunks per add/update/remove polimorfico.
 *
 * Dopo ogni mutazione, il thunk dispatcha il fetch dell'entita' owner
 * corretta in base a `className` per rinfrescare il Redux state (che
 * contiene l'array emailAddresses denormalizzato dentro Person/Business).
 *
 * Instradamento API per-owner (entrambi nested per-owner, niente flat):
 *   - className === 'Person'   -> personEmailAddressApi
 *   - className === 'Business' -> businessEmailAddressApi
 *
 * Questo approccio e' pessimistico: la mutazione nel Redux state avviene
 * solo dopo che la chiamata API e il refetch parent sono completati.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { extractError } from '../../store/thunkHelpers';
import { personEmailAddressApi } from '../person/personEmailAddressApi';
import { businessEmailAddressApi } from '../business/businessEmailAddressApi';
import type {
  EmailAddress,
  CreateEmailAddressPayload,
  UpdateEmailAddressPayload,
  RemoveEmailAddressPayload,
  EmailAddressOwnerClassName,
} from './emailAddressTypes';
import { fetchPerson } from '../person/personThunks';
import { fetchBusiness } from '../business/businessThunks';
import type { AppDispatch } from '../../store/store';

function dispatchOwnerRefresh(dispatch: AppDispatch, className: EmailAddressOwnerClassName, classPK: number): void {
  if (className === 'Person') dispatch(fetchPerson(classPK));
  else if (className === 'Business') dispatch(fetchBusiness(classPK));
}

export const addEmailAddress = createAsyncThunk<EmailAddress, CreateEmailAddressPayload>(
  'emailAddresses/add',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const body = { address: payload.address, primary: payload.primary };
      const created = payload.className === 'Person'
        ? await personEmailAddressApi.create(payload.classPK, body)
        : await businessEmailAddressApi.create(payload.classPK, body);
      dispatchOwnerRefresh(dispatch as AppDispatch, payload.className, payload.classPK);
      return created;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const updateEmailAddress = createAsyncThunk<EmailAddress, UpdateEmailAddressPayload>(
  'emailAddresses/update',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const body = { address: payload.address, primary: payload.primary };
      const updated = payload.className === 'Person'
        ? await personEmailAddressApi.update(payload.classPK, payload.emailAddressId, body)
        : await businessEmailAddressApi.update(payload.classPK, payload.emailAddressId, body);
      dispatchOwnerRefresh(dispatch as AppDispatch, payload.className, payload.classPK);
      return updated;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

export const removeEmailAddress = createAsyncThunk<number, RemoveEmailAddressPayload>(
  'emailAddresses/remove',
  async ({ emailAddressId, className, classPK }, { dispatch, rejectWithValue }) => {
    try {
      if (className === 'Person') {
        await personEmailAddressApi.remove(classPK, emailAddressId);
      } else {
        await businessEmailAddressApi.remove(classPK, emailAddressId);
      }
      dispatchOwnerRefresh(dispatch as AppDispatch, className, classPK);
      return emailAddressId;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);
